import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import moment from 'moment-timezone';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import Typography from 'Components/UI/Typography';
import Button from '@material-ui/core/Button';
import { ArrowLeft } from '@material-ui/icons';
import { MenuItem, Paper, Select } from '@material-ui/core';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import useResource from 'Hooks/useResource';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import {
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam,
  useAgentSchedulerDispatchContext,
  useAgentSchedulerStateContext,
} from 'Context/NewAgentSchedulerContext';
import SkeletonOverlay from 'Components/UI/SkeletonOverlay';
import { MCSchedulerCalendarEventOverride } from 'Overrides/MCSchedulerCalendarEvent';
import { MCSchedulerRescheduleCalendarEventOverride } from 'Overrides/MCSchedulerRescheduleCalendarEvent';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
import MuiCheckbox from '@material-ui/core/Checkbox';
import NewMeetingLocationMetaOverride
  from 'Components/AdvisorApp/Viewport/Scheduler/NewMeeting/NewMeetingLocation/NewMeetingLocationMetaOverride';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  newMeetingInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: theme.typography.fontWeightExtraBold,
    color: theme.palette.facet.primaryBlue,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginRight: theme.spacing(1),
  },
  titleText: {
    color: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightMedium,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  attendees: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  attendeesTitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  backButton: {
    color: theme.palette.facet.primaryBlue,
  },
  meetingTitleInputContainer: {
    display: 'flex',
  },
  meetingTitleInput: {
    flex: 1,
  },
  meetingInfoContainer: {
    backgroundColor: theme.palette.facet.lightGray,
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
  meetingInfoTitle: {
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightMedium,
  },
  bookMeetingButton: {
    backgroundColor: theme.palette.facet.accentGreen,
    color: theme.palette.common.white,
    flex: 1,
    marginTop: theme.spacing(1),
    width: '100%',
  },
  bookMeetingButtonLabel: {
    display: 'flex',
    flexDirection: 'column',
    '& > p': {
      margin: 'unset',
    },
  },
  bookMeetingText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  metaControl: {
    width: '100%',
  },
  timeTextContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  timeSelect: {
    marginLeft: theme.spacing(1),
  },
  descriptionLabel: {
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.8rem',
  },
  override: {
    marginLeft: theme.spacing(1),
  },
  plannerTimezone: {
    color: theme.palette.facet.primaryBlue,
    marginLeft: theme.spacing(1),
  },
});

const selectTimeFormat = 'hh:mm a';

const getStartTimeOptions = (start, end, duration, bufferLength, displayFullDay) => {
  let startTime;
  let endTime;
  if (displayFullDay) {
    startTime = moment(start).startOf('day').add(bufferLength, 'minute');
    endTime = moment(end).endOf('day').subtract(duration, 'minute').subtract(bufferLength, 'minute');
  } else {
    startTime = moment(start).add(bufferLength, 'minute');
    endTime = moment(end).subtract(duration, 'minute').subtract(bufferLength, 'minute');
  }

  const segmentCount = (endTime.diff(startTime, 'minutes') / 15) + 1;

  const options = [];
  options.push(moment(startTime).format(selectTimeFormat));
  for (let i = 1; i < segmentCount; i++) {
    options.push(moment(startTime).add(i * 15, 'minute').format(selectTimeFormat));
  }

  return options;
};

const NewMeeting = ({
  classes,
  className,
}) => {
  const { householdResource } = useSimpleHousehold();
  const { resource: newEvent, setAttribute: setNewEventAttribute, initializeResource: initializeNewEvent } = useResource();
  const {
    currentCollection,
    selectedGapStart,
    selectedGapEnd,
    selectedMeetingDuration,
    selectedBufferDuration,
    prevLink,
    isAvailable,
  } = useAgentSchedulerStateContext();
  const dispatch = useAgentSchedulerDispatchContext();
  let calendarEvent;
  const {
    logEvent,
    logResourceEventSuccess,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent();
  const [displayFullDay, setDisplayFullDay] = useState(false);
  const timeOptions = getStartTimeOptions(
    selectedGapStart,
    selectedGapEnd,
    Number(selectedMeetingDuration),
    Number(selectedBufferDuration),
    displayFullDay,
  );
  const [startTime, setStartTime] = useState(timeOptions[0]);
  const [savingEvent, setSavingEvent] = useState(false);
  const isRescheduling = currentCollection.resources().length() > 0;
  const [fetchingNewTitle, setFetchingNewTitle] = useState(false);
  const currentTimezoneString = moment().tz(moment.tz.guess()).format('z');
  const householdTimezone = householdResource?.attr('timeZone');

  useEffect(() => {
    if (currentCollection.resources().length() === 0) {
      calendarEvent = currentCollection.create({ metaOverrideSet: MCSchedulerCalendarEventOverride });
    } else {
      calendarEvent = currentCollection.resources().head().overrideMeta(MCSchedulerRescheduleCalendarEventOverride);
    }
    initializeNewEvent(calendarEvent);
  }, [currentCollection]);

  const dateTitle = moment(selectedGapStart).format('dddd, MMMM Do, YYYY');

  const getFormattedClientTime = () => {
    const momentStart = moment(selectedGapStart);
    return moment(startTime, selectTimeFormat)
      .tz(householdTimezone)
      .month(momentStart.month())
      .date(momentStart.date())
      .year(momentStart.year())
      .format('h:mmA z');
  };

  const getNewMetaBasedOnEventType = (value) => {
    return newEvent.query('getCalendarEvents', (link) => {
      return link.equal('eventType', value).equal('limit', 0).equal('householdId', householdResource.id()).get();
    });
  };

  const updateNewEventAttr = attr => (value) => {
    setNewEventAttribute(attr, value);
  };

  const updateEventMeetingType = attr => async (value) => {
    setNewEventAttribute(attr, value);
    setFetchingNewTitle(true);
    try {
      const resourceWithNewMeta = await getNewMetaBasedOnEventType(value);
      const newTitle = resourceWithNewMeta.meta().field('title').defaultValue() || '';
      setNewEventAttribute('title', newTitle);
      setFetchingNewTitle(false);
    } catch (e) {
      setFetchingNewTitle(false);
      displayErrorMessage('Unable to get default meeting title');
    }
  };

  const handleStartSelection = (newValue) => {
    logEvent('Clicked Change Start Time Selection', { startTime: moment(selectedGapStart).format(selectTimeFormat), newValue });
    setStartTime(newValue);
  };

  const handleBookMeeting = async () => {
    if (!savingEvent) {
      const momentStart = moment(selectedGapStart);
      const eventStartTime = moment(startTime, selectTimeFormat);

      const eventStart = momentStart.set(({ hour: eventStartTime.get('hour'), minute: eventStartTime.get('minute') }));
      const endTimeFromStart = moment(eventStart).add(selectedMeetingDuration, 'minute').toISOString();
      try {
        setSavingEvent(true);
        dispatch({ type: 'loadingStart' });
        // if the selected gap is available and display full day is set to true,
        // set the isOverride attribute to true
        if (isAvailable === 'true' && displayFullDay) {
          newEvent.attr('isOverride', 'true');
        }
        newEvent.attr('startTime', eventStart.toISOString());
        newEvent.attr('endTime', endTimeFromStart);
        newEvent.attr('meetingType', 'zoom');
        newEvent.attr('subject', newEvent.attr('title'));
        newEvent.attr('attendees', newEvent.attr('attendees'));
        newEvent.attr('priority', '1');
        newEvent.attr('bufferLength', selectedBufferDuration);
        const [submittedCalendarEvent] = await newEvent.submit();
        displaySuccess('Meeting saved');
        logResourceEventSuccess('Meeting saved from MC Scheduler', newEvent, {
          isAvailable,
          override: newEvent.attr('isOverride'),
        });
        setNewLinkQueryParam(dispatch, submittedCalendarEvent.link('self').url());
        dispatch({ type: 'setMeetingBooked' });
        fetchDataFromLinkQueryParam(dispatch);
      } catch (e) {
        logResourceEventFailureWithError('Meeting saved from MC Scheduler', newEvent, e);
        // if the error is an array, use the detailed error message from the first error
        if (Array.isArray(e.errors)) {
          const [error] = e.errors;
          displayErrorMessage(e, error?.detail);
        } else {
          displayErrorMessage(e, e.errors);
        }
        dispatch({ type: 'setView', view: 'newMeeting', newCollection: currentCollection });
      } finally {
        setSavingEvent(false);
        dispatch({ type: 'loadingEnd' });
      }
    }
  };

  const handleClickBackButton = () => {
    logEvent('Clicked Back Button to go to Scheduler Weekly View');
    setNewLinkQueryParam(dispatch, prevLink);
    fetchDataFromLinkQueryParam(dispatch);
  };

  const saveButtonDisabled = () => !newEvent || savingEvent;
  const titleSkeleton = !fetchingNewTitle;

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.content}>
        <Button
          className={classes.backButton}
          startIcon={<ArrowLeft />}
          onClick={handleClickBackButton}
        >
          Back
        </Button>
        <div className={classes.newMeetingInfo}>
          <div className={classes.timeTextContainer}>
            <Typography variant="h3" className={classes.title}>
              Date:
            </Typography>
            <Typography variant="h3" className={classes.titleText} test="new-meeting-date-header">
              {dateTitle}
            </Typography>
          </div>
          <div className={classes.timeTextContainer}>
            <Typography variant="h3" className={classes.title}>
              Time:
            </Typography>
            <Select
              variant="outlined"
              className={classes.timeSelect}
              onChange={e => handleStartSelection(e.target.value)}
              value={startTime}
              test="new-meeting-time-select"
              inputProps={{
                test: 'new-meeting-time-select-input',
              }}
            >
              {timeOptions.map(time => (
                <MenuItem value={time} key={time}>{time}</MenuItem>
              ))}
            </Select>
            <Typography variant="h3" className={classes.plannerTimezone}>
              {currentTimezoneString}
            </Typography>
            <MuiFormControlLabel
              label="Show all time slots"
              className={classes.override}
              hidden={newEvent?.meta().field('isOverride').attr('hidden')}
              control={
                <MuiCheckbox
                  className={classes.checkbox}
                  variant="outlined"
                  checked={displayFullDay}
                  onClick={() => setDisplayFullDay(value => !value)}
                />
                }
            />
          </div>
          {householdTimezone && (
            <div className={classes.timeTextContainer}>
              <Typography variant="h3" className={classes.title}>
                    Client Time:
              </Typography>
              <Typography variant="h3" className={classes.titleText}>
                {getFormattedClientTime()}
              </Typography>
            </div>
          )}
          <SkeletonOverlay loading={newEvent}>
            <MetaControl resource={newEvent} attrName={'location'} metaOverrideSet={NewMeetingLocationMetaOverride} />
          </SkeletonOverlay>
        </div>
        <SkeletonOverlay loading={newEvent}>
          <MetaControl attrName="eventType" resource={newEvent} className={classes.metaControl} onChange={updateEventMeetingType('eventType')} test="scheduler-new-meeting-event-type-input" />
        </SkeletonOverlay>
        <div className={classes.meetingTitleInputContainer}>
          <SkeletonOverlay loading={titleSkeleton} className={classes.metaControl}>
            {newEvent ? <MetaControl attrName="title" resource={newEvent} className={classes.metaControl} onChange={updateNewEventAttr('title')} test="scheduler-new-meeting-title-input" /> : null }
          </SkeletonOverlay>
        </div>
        <div className={classes.attendees}>
          <SkeletonOverlay loading={newEvent}>
            <MetaControl
              attrName="attendees"
              resource={newEvent}
              className={classes.metaControl}
              onChange={updateNewEventAttr('attendees')}
              test="scheduler-new-meeting-attendees"
              SelectProps={{ test: 'scheduler-new-meeting-attendees-select' }}
            />
          </SkeletonOverlay>
        </div>
        <Paper className={classes.meetingInfoContainer}>
          <Typography variant="h3" className={classes.meetingInfoTitle}>
            For internal purposes only
          </Typography>
          <SkeletonOverlay loading={newEvent}>
            <MetaControl attrName="bestNextStepId" resource={newEvent} className={classes.metaControl} onChange={updateNewEventAttr('bestNextStepId')} test="scheduler-new-meeting-best-next-step-id-select" />
          </SkeletonOverlay>
        </Paper>
        <Button disabled={saveButtonDisabled()} variant="contained" disableElevation className={classes.bookMeetingButton} onClick={handleBookMeeting} test="scheduler-new-meeting-book-meeting-button">
          <div className={classes.bookMeetingButtonLabel}>
            <p className={classes.bookMeetingText}>{isRescheduling ? 'Reschedule' : 'Book' } Meeting</p>
            <p>(This will send an invite to the client)</p>
          </div>
        </Button>
      </div>
    </div>
  );
};

NewMeeting.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

NewMeeting.defaultProps = {
  className: '',
};

export default withStyles(styles)(NewMeeting);
